import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { TextareaAutosize } from "@/components/ui/textarea-autosize";
import { useToast, useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { UserInstructionDTO } from "@shaarp/common";
import { useEffect, useState } from "react";
import { ButtonLoading } from "../button-loading";
import PageSkeleton from "../page-skeleton";

export function UserSettingInstruction() {
  const toastError = useToastError();
  const api = useApi();
  const { toast } = useToast();
  const { t } = useTranslation();

  const [userInstruction, setUserInstruction] =
    useState<UserInstructionDTO | null>(null);
  const [newInstruction, setNewInstruction] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const { data } = await api.get<UserInstructionDTO>(
          `/my/user/instruction`
        );
        setUserInstruction(data);
        setNewInstruction(data.content || "");
      } catch (error) {
        toastError();
        setUserInstruction(null);
        setNewInstruction("");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const save = async () => {
    try {
      setIsLoading(true);
      await api.post(`/my/user/instruction`, {
        content: newInstruction,
      });
      toast({
        variant: "default",
        title: t("settings.instruction.saveSuccess.title"),
        description: t("settings.instruction.saveSuccess.description"),
      });
    } catch (error) {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("settings.instruction.title")}</CardTitle>
        <CardDescription>
          {t("settings.instruction.description")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading && <PageSkeleton />}
        {!isLoading && (
          <TextareaAutosize
            placeholder={t("settings.instruction.placeholder")}
            rows={20}
            value={newInstruction || ""}
            onChange={(e) => setNewInstruction(e.target.value)}
            className="flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          />
        )}
      </CardContent>
      <CardFooter className="flex justify-between px-6 py-4 border-t">
        <ButtonLoading onClick={save} isLoading={isLoading}>
          {t("settings.instruction.saveButton")}
        </ButtonLoading>
        {userInstruction?.updatedAt && userInstruction?.lastUpdatedBy && (
          <div className="text-sm text-gray-400">
            {t("settings.instruction.lastUpdate", {
              date: new Date(userInstruction.updatedAt).toLocaleString(),
              name: userInstruction.lastUpdatedBy.fullName,
            })}
          </div>
        )}
      </CardFooter>
    </Card>
  );
}
